import { Form, Formik } from 'formik'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IoInformationCircleOutline } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  useCreateHelpdeskTicketMutation,
  useGetAllUserHelpdeskTicketsQuery,
  useGetHelpdeskCategoriesQuery,
  useGetHelpdeskPrioritiesQuery,
} from '../../api/services/helpdeskApi'
import { getHelpDeskTicketValidationSchema } from '../../validation/helpDeskTicketValidation'
import AttachmentUploader from './AttachmentUploader'
import FormInput from './FormInput'
import FormSelect from './FormSelect'
import FormTextarea from './FormTextarea'

const initialValues = {
  subject: '',
  description: '',
  category_id: null,
  priority_id: null,
  reference_number: '',
  related_ticket_id: null,
}

const Section = ({ title, children }) => (
  <section className="text-left">
    <h3 className="mb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
    <div className="rounded-lg border border-slate-200 bg-white p-4">{children}</div>
  </section>
)

const HelpDeskTicketForm = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation()
  const company = useSelector((state) => state.company.company)
  const [attachments, setAttachments] = useState([])

  const {
    data: categoriesResponse,
    isLoading: isLoadingCategories,
    isError: isCategoriesError,
  } = useGetHelpdeskCategoriesQuery()
  const {
    data: prioritiesResponse,
    isLoading: isLoadingPriorities,
    isError: isPrioritiesError,
  } = useGetHelpdeskPrioritiesQuery()
  const { data: allUserTickets = [] } = useGetAllUserHelpdeskTicketsQuery()
  const [createTicket, { isLoading: isSubmitting }] = useCreateHelpdeskTicketMutation()

  const isLoadingLookups = isLoadingCategories || isLoadingPriorities
  const isLookupsError = isCategoriesError || isPrioritiesError

  const categoryOptions = useMemo(
    () => categoriesResponse?.data?.map((item) => ({ value: item.id, label: item.name })) ?? [],
    [categoriesResponse?.data],
  )

  const priorityOptions = useMemo(
    () => prioritiesResponse?.data?.map((item) => ({ value: item.id, label: item.name })) ?? [],
    [prioritiesResponse?.data],
  )

  const relatedTicketOptions = useMemo(
    () =>
      allUserTickets.map((ticket) => ({
        value: ticket.ticket_id,
        label: `${ticket.ticket_number} — ${ticket.subject}`,
      })),
    [allUserTickets],
  )

  const handleSubmit = async (values, { resetForm }) => {
    if (!company?.id) {
      toast.error(t('helpdesk.form.no_company'))
      return
    }

    try {
      const formData = new FormData()
      formData.append('company_id', String(company.id))
      formData.append('subject', values.subject.trim())
      formData.append('description', values.description.trim())
      formData.append('category_id', String(values.category_id))
      formData.append('priority_id', String(values.priority_id))

      const referenceNumber = values.reference_number.trim()
      if (referenceNumber) {
        formData.append('reference_number', referenceNumber)
      }

      if (values.related_ticket_id != null) {
        formData.append('related_ticket_id', String(values.related_ticket_id))
      }

      attachments.forEach((file) => {
        formData.append('files', file)
      })

      const ticket = await createTicket(formData).unwrap()
      toast.success(t('helpdesk.form.success'))
      resetForm()
      setAttachments([])
      onSuccess?.(ticket)
    } catch (err) {
      const validationMessage = err?.data?.errors?.[0]?.message
      toast.error(validationMessage || err?.data?.message || t('helpdesk.form.error'))
    }
  }

  if (isLoadingLookups) {
    return <p className="py-6 text-left text-sm text-gray-600">{t('helpdesk.form.loading_lookups')}</p>
  }

  if (isLookupsError) {
    return <p className="py-6 text-left text-sm text-red-600">{t('helpdesk.form.lookups_error')}</p>
  }

  const hasLookups = categoryOptions.length > 0 && priorityOptions.length > 0
  const required = <span className="ml-0.5 text-red-500">*</span>

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getHelpDeskTicketValidationSchema(t)}
      onSubmit={handleSubmit}
    >
      <Form className="text-left">
        <div className="mb-5 flex items-start gap-3 rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <IoInformationCircleOutline className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
          <p>{t('helpdesk.form.info')}</p>
        </div>

        <div className="space-y-6">
          {!hasLookups && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              {t('helpdesk.form.no_lookups')}
            </p>
          )}

          <Section title={t('helpdesk.form.section.details')}>
            <FormInput
              name="subject"
              label={
                <>
                  {t('helpdesk.form.subject')}
                  {required}
                </>
              }
              placeholder={t('helpdesk.form.subject_placeholder')}
              maxLength={150}
            />
            <FormTextarea
              name="description"
              label={
                <>
                  {t('helpdesk.form.description')}
                  {required}
                </>
              }
              placeholder={t('helpdesk.form.description_placeholder')}
            />
          </Section>

          <Section title={t('helpdesk.form.section.classification')}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormSelect
                name="category_id"
                label={
                  <>
                    {t('helpdesk.form.category')}
                    {required}
                  </>
                }
                options={categoryOptions}
                placeholder={t('helpdesk.form.select_category')}
              />
              <FormSelect
                name="priority_id"
                label={
                  <>
                    {t('helpdesk.form.priority')}
                    {required}
                  </>
                }
                options={priorityOptions}
                placeholder={t('helpdesk.form.select_priority')}
              />
            </div>
            <FormInput
              name="reference_number"
              label={t('helpdesk.form.reference_number')}
              placeholder={t('helpdesk.form.reference_number_placeholder')}
              maxLength={100}
            />
            <FormSelect
              name="related_ticket_id"
              label={t('helpdesk.form.related_ticket')}
              options={relatedTicketOptions}
              placeholder={t('helpdesk.form.related_ticket_placeholder')}
              allowEmpty
            />
          </Section>

          <Section title={t('helpdesk.form.section.attachments')}>
            <AttachmentUploader files={attachments} onChange={setAttachments} hideLabel />
          </Section>
        </div>

        <div className="-mx-4 mt-6 flex items-center justify-end gap-2 border-t border-[#D5D9EC] bg-[#F7F7F7] px-4 py-4">
          {onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              {t('helpdesk.form.cancel')}
            </button>
          ) : null}
          <button
            type="submit"
            disabled={isSubmitting || !hasLookups || !company?.id}
            className="rounded-md bg-[#006EAB] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#005a8f] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? t('helpdesk.form.submitting') : t('helpdesk.form.submit')}
          </button>
        </div>
      </Form>
    </Formik>
  )
}

export default HelpDeskTicketForm
